/* globals window */
import React from 'react';
import PropTypes from 'prop-types';

import withMessages, { FormattedHTMLMessage } from 'component-utilities/intl';
import { formatTZDate } from 'component-utilities/dates/dates';

import IfFeature from '../../Containers/IfFeature';

import Breadcrumbs from '../../Breadcrumbs/Breadcrumbs';

import PageContent from '../Components/PageContent';
import ContentHeader from '../Components/ContentHeader/ContentHeader';
import ContentTitle from '../Components/ContentHeader/ContentTitle';

import FlagsContainer from '../../Flags/FlagsContainer';
import FlagsContainerInline from '../../Flags/FlagsContainerInline';
import FlagsSmall from '../../Flags/FlagsSmall';
import FlagsLarge from '../../Flags/FlagsLarge';
import FlagRandomized from '../../Flags/FlagRandomized';
import FlagCustom from '../../Flags/FlagCustom';

import DetailsContentBody from './DetailsContentBody';

import Actions from '../../Buttons/ActionButtons/Actions';
import ActionSaveAsNew from '../../Buttons/ActionButtons/ActionSaveAsNew';
import ActionDeleteSearch from '../../Buttons/ActionButtons/ActionDeleteSearch';
import ActionPrint from '../../Buttons/ActionButtons/ActionPrint';

import FormContainer from '../../Forms/FormContainer';
import FormContainerTitle from '../../Forms/FormContainerTitle';
import FormContainerInstructions from '../../Forms/FormContainerInstructions';
import FormRow from '../../Forms/FormRow';
import FormContent from '../../Forms/FormContent';
import FormItemSmall from '../../Forms/FormItemSmall';
import FormItemLarge from '../../Forms/FormItemLarge';
import FormItemFull from '../../Forms/FormItemFull';
import FormLabel from '../../Forms/FormLabel';
import FormText from '../../Forms/FormText';
import FormField from '../../Forms/FormField';
import FormFieldPercentage from '../../Forms/FormFieldPercentage';
import FormButtons from '../../Forms/FormButtons';
import FormErrorText from '../../Forms/FormErrorText';

import List from '../../Lists/List';
import ListItem from '../../Lists/ListItem';

import InputField from '../../Forms/InputFields/InputFieldV2';
import Textarea from '../../Forms/Textarea/Textarea';

import PrimaryButton from '../../Buttons/PrimaryButton';
import ButtonsSpacer from '../../Buttons/Spacer';
import SecondaryButton from '../../Buttons/SecondaryButton';

import viewLogic from './DetailsPageViewLogic';

/* eslint-disable no-underscore-dangle */
IfFeature.featureData = () => window.__FEATURES__;
/* eslint-enable no-underscore-dangle */

export const BASE_KEY = 'components.templates.details_page';

function DetailsPageComponent(props) {
    const {
        getMessage,
        processing,
        breadcrumbs,
        actionsConfig,
        formConfig,
        searchConfig,
        userAccessConfig,
        buttonsConfig,

        criteriaLabelProps,
        resultsText,
        resultsValues,
        totalEmailsText,
        formFieldPercentageInputProps,
        contentTitle,
        userAccessLinkProps,
        totalEmails,
        randomlySelected,
        isRandomizeSearchEnabled,
        isUserAccessEnabled,
    } = viewLogic(props, { BASE_KEY, IfFeature });

    return (
        <PageContent>
            <ContentHeader>
                <Breadcrumbs breadcrumbs={breadcrumbs} />
                <ContentTitle text={contentTitle}>
                    {searchConfig.isRandomized && (
                        <div style={{ marginTop: '5px' }}>
                            <FlagsContainer>
                                <FlagsSmall>
                                    <FlagRandomized />
                                </FlagsSmall>
                            </FlagsContainer>
                        </div>
                    )}
                </ContentTitle>
            </ContentHeader>
            <DetailsContentBody>
                <Actions>
                    {actionsConfig.print && <ActionPrint {...actionsConfig.print} />}
                    {actionsConfig.saveAsNew && <ActionSaveAsNew {...actionsConfig.saveAsNew} />}
                    {actionsConfig.deleteSearch && <ActionDeleteSearch {...actionsConfig.deleteSearch} />}
                </Actions>
                <FormContainer>
                    {processing}
                    <h5 className="timestamp">
                        <FormattedHTMLMessage
                            id={`${BASE_KEY}.timestamp`}
                            values={{
                                updated:
                  (!!searchConfig.details && searchConfig.details.lastModifiedDate) ||
                  formatTZDate(Date.now()),
                            }}
                        />
                    </h5>
                    <FormContainerTitle>
                        {getMessage(`${BASE_KEY}.basic_information`)}
                    </FormContainerTitle>
                    <FormRow>
                        <FormContent>
                            <FormItemLarge>
                                <FormLabel required>
                                    {getMessage(`${BASE_KEY}.search_name`)}
                                </FormLabel>
                                <InputField
                                    focused
                                    data-field="search-name"
                                    status={!searchConfig.userCanEditFields ? 'disabled' : ''}
                                    {...formConfig.searchName}
                                />
                            </FormItemLarge>
                        </FormContent>
                    </FormRow>
                    <FormRow>
                        <FormContent>
                            <FormItemLarge>
                                <FormLabel>
                                    {getMessage(`${BASE_KEY}.description`)}
                                </FormLabel>
                                <Textarea
                                    focused={false}
                                    data-field="search-description"
                                    disabled={!searchConfig.userCanEditFields}
                                    {...formConfig.searchDescription}
                                />
                            </FormItemLarge>
                        </FormContent>
                    </FormRow>
                    {searchConfig.details && (
                        <FormRow>
                            <FormContent type="inline">
                                <FormItemSmall>
                                    <FormLabel>
                                        {getMessage(`${BASE_KEY}.created_by`)}
                                    </FormLabel>
                                    <FormField data-value="created-by">
                                        {searchConfig.details.createdBy}
                                    </FormField>
                                </FormItemSmall>
                                <FormItemSmall>
                                    <FormLabel>
                                        {getMessage(`${BASE_KEY}.creation_date`)}
                                    </FormLabel>
                                    <FormField data-value="creation-date">
                                        {searchConfig.details.creationDate}
                                    </FormField>
                                </FormItemSmall>
                            </FormContent>
                            <FormContent type="inline">
                                <FormItemSmall>
                                    <FormLabel>
                                        {getMessage(`${BASE_KEY}.last_modified_by`)}
                                    </FormLabel>
                                    <FormField data-value="last-modified-by">
                                        {searchConfig.details.lastModifiedBy}
                                    </FormField>
                                </FormItemSmall>
                                <FormItemSmall>
                                    <FormLabel>
                                        {getMessage(`${BASE_KEY}.last_modified_date`)}
                                    </FormLabel>
                                    <FormField data-value="last-modified-date">
                                        {searchConfig.details.lastModifiedDate}
                                    </FormField>
                                </FormItemSmall>
                            </FormContent>
                        </FormRow>
                    )}
                    <FormContainerTitle>
                        {getMessage(`${BASE_KEY}.search_summary`)}
                    </FormContainerTitle>
                    <FormRow>
                        <FormContent type="stacked">
                            <FormItemFull>
                                <FormLabel {...criteriaLabelProps}>
                                    {getMessage(`${BASE_KEY}.criteria`)}
&nbsp;&nbsp;
                                </FormLabel>
                                <FormText>
                                    {searchConfig.criteriaList || ''}
                                </FormText>
                            </FormItemFull>
                            <FormItemFull>
                                <FormLabel>
                                    {getMessage(`${BASE_KEY}.hold`)}
                                </FormLabel>
                                <List>
                                    <ListItem>
                                        <FormText>
                                            <FormattedHTMLMessage
                                                id={`${BASE_KEY}.hold_details`}
                                                values={resultsValues}
                                            />
                                        </FormText>
                                    </ListItem>
                                </List>
                            </FormItemFull>
                            <FormItemFull>
                                <FormLabel>
                                    {getMessage(`${BASE_KEY}.tags`)}
                                </FormLabel>
                                <List>
                                    <ListItem>
                                        <FormText>
                                            <FormattedHTMLMessage
                                                id={`${BASE_KEY}.tags_reviewed_details`}
                                                values={resultsValues}
                                            />
                                        </FormText>
                                    </ListItem>
                                    <ListItem>
                                        <FormText>
                                            <FormattedHTMLMessage
                                                id={`${BASE_KEY}.tags_need_review_details`}
                                                values={resultsValues}
                                            />
                                        </FormText>
                                    </ListItem>
                                </List>
                            </FormItemFull>
                            <FormItemFull>
                                <FormLabel>
                                    {getMessage(`${BASE_KEY}.results`)}
                                </FormLabel>
                                <List>
                                    <ListItem>
                                        <FormText>
                                            <FormattedHTMLMessage
                                                id={`${BASE_KEY}.${resultsText}`}
                                                values={resultsValues}
                                            />
                                        </FormText>
                                    </ListItem>
                                </List>
                            </FormItemFull>
                        </FormContent>
                    </FormRow>
                    {isRandomizeSearchEnabled && (
                        <FormContainerTitle>
                            {getMessage(`${BASE_KEY}.randomize_results`)}
                        </FormContainerTitle>
                    )}
                    {isRandomizeSearchEnabled && (
                        <FormContainerInstructions>
                            {getMessage(`${BASE_KEY}.randomize_results_instruction`)}
                        </FormContainerInstructions>
                    )}
                    {isRandomizeSearchEnabled && (
                        <FormRow>
                            <FormContent type="stacked">
                                <FormItemLarge>
                                    <FormLabel>
                                        {getMessage(`${BASE_KEY}.set_percentage`)}
                                    </FormLabel>
                                    <FormFieldPercentage
                                        inputProps={{
                                            ...formFieldPercentageInputProps,
                                            'data-field': 'search-sample-percentage',
                                        }}
                                    >
                                        {!!formConfig.samplePercentage.value && (
                                            <FormText inline>
                                                <FormattedHTMLMessage
                                                    id={`${BASE_KEY}.${totalEmailsText}`}
                                                    values={{ total: Number(totalEmails).toLocaleString() }}
                                                />
                                            </FormText>
                                        )}
                                        {searchConfig.results.isSamplePercentageInValidRange && (
                                            <FlagsContainerInline>
                        &nbsp;&nbsp;&nbsp;
                                                <FlagsLarge>
                                                    <FlagCustom>
                                                        <FormattedHTMLMessage
                                                            id={`${BASE_KEY}.randomly_selected`}
                                                            values={{
                                                                sampled: Number(randomlySelected).toLocaleString(),
                                                            }}
                                                        />
                                                    </FlagCustom>
                                                </FlagsLarge>
                                            </FlagsContainerInline>
                                        )}
                                    </FormFieldPercentage>
                                    {searchConfig.results.samplePercentageErrors && (
                                        <FormErrorText
                                            field="sample-percentage"
                                            error={`${BASE_KEY}.form_errors.randomize_results_invalid.${searchConfig.results.samplePercentageErrors}`}
                                            attributes={{
                                                'data-random-search-error': searchConfig.results.samplePercentageErrors,
                                            }}
                                        />
                                    )}
                                </FormItemLarge>
                            </FormContent>
                        </FormRow>
                    )}
                    {isUserAccessEnabled && (
                        <FormContainerTitle>
                            {getMessage(`${BASE_KEY}.user_access`)}
                        </FormContainerTitle>
                    )}
                    {isUserAccessEnabled && (
                        <FormContainerInstructions>
                            {getMessage(`${BASE_KEY}.user_access_instruction`)}
                        </FormContainerInstructions>
                    )}
                    {isUserAccessEnabled && (
                        <FormRow>
                            <FormContent type="stacked">
                                <FormItemLarge>
                                    <FormLabel
                                        attributes={{
                                            'data-user-access': userAccessConfig.users.length,
                                        }}
                                        style={{ marginBottom: '6px' }}
                                        link={userAccessLinkProps}
                                    >
                                        {`${getMessage(`${BASE_KEY}.number_of_users`)} (${
                                            userAccessConfig.users.length
                                        })`}
                    &nbsp;&nbsp;
                                    </FormLabel>
                                    {!userAccessConfig.users.length && <FormText>
&nbsp;&nbsp;
                                    </FormText>}
                                    <FormText>
                                        {userAccessConfig.users.join(', ')}
                                    </FormText>
                                </FormItemLarge>
                            </FormContent>
                        </FormRow>
                    )}

                    <FormRow>
                        <FormButtons>
                            <PrimaryButton data-action="save-search" {...buttonsConfig.save}>
                                {getMessage(`${BASE_KEY}.save`)}
                            </PrimaryButton>
                            <ButtonsSpacer />
                            <SecondaryButton data-action="cancel-search" {...buttonsConfig.cancel}>
                                {getMessage(`${BASE_KEY}.cancel`)}
                            </SecondaryButton>
                        </FormButtons>
                    </FormRow>
                </FormContainer>
            </DetailsContentBody>
        </PageContent>
    );
}

DetailsPageComponent.propTypes = {
    isNewSearch: PropTypes.bool.isRequired,
    getMessage: PropTypes.func.isRequired,
    processing: PropTypes.any,
    breadcrumbs: PropTypes.array.isRequired,
    actionsConfig: PropTypes.shape({
        saveAsNew: PropTypes.shape({
            status: PropTypes.string.isRequired,
            onClick: PropTypes.func.isRequired,
        }),
        deleteSearch: PropTypes.shape({
            status: PropTypes.string.isRequired,
            onClick: PropTypes.func.isRequired,
        }),
    }).isRequired,
    formConfig: PropTypes.shape({
        searchName: PropTypes.shape({
            value: PropTypes.string.isRequired,
            onChange: PropTypes.func.isRequired,
        }),
        searchDescription: PropTypes.shape({
            value: PropTypes.string.isRequired,
            onChange: PropTypes.func.isRequired,
        }),
        samplePercentage: PropTypes.shape({
            value: PropTypes.any.isRequired,
            onChange: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    searchConfig: PropTypes.shape({
        details: PropTypes.shape({
            createdBy: PropTypes.string.isRequired,
            creationDate: PropTypes.string.isRequired,
            lastModifiedBy: PropTypes.string.isRequired,
            lastModifiedDate: PropTypes.string.isRequired,
        }),
        userCanEditFields: PropTypes.bool.isRequired,
        userCanEditSearch: PropTypes.bool.isRequired,
        onEdit: PropTypes.func.isRequired,
        searchName: PropTypes.string.isRequired,
        isRandomized: PropTypes.bool.isRequired,
        criteriaList: PropTypes.any,
        results: PropTypes.shape({
            total: PropTypes.number.isRequired,
            time: PropTypes.string.isRequired,
            isSamplePercentageInValidRange: PropTypes.bool.isRequired,
            randomizedDetails: PropTypes.shape({
                sampled: PropTypes.string,
                sampleFullCount: PropTypes.string,
                samplePercentage: PropTypes.string,
                date: PropTypes.string,
            }),
        }),
    }).isRequired,
    userAccessConfig: PropTypes.shape({
        users: PropTypes.array.isRequired,
        onManageUserAccess: PropTypes.func.isRequired,
    }).isRequired,
    buttonsConfig: PropTypes.shape({
        save: PropTypes.shape({
            disabled: PropTypes.bool.isRequired,
            onClick: PropTypes.func.isRequired,
        }),
        cancel: PropTypes.shape({
            disabled: PropTypes.bool.isRequired,
            onClick: PropTypes.func.isRequired,
        }),
    }),
};

DetailsPageComponent.defaultProps = {
    breadcrumbs: [],
    actionsConfig: {},
    formConfig: {},
    searchConfig: {},
    userAccessConfig: {},
    buttonsConfig: {},
    processing: null,
};

const DetailsPage = withMessages(DetailsPageComponent);

export default DetailsPage;
